import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import { useForm } from 'react-hook-form';
import api from '../../utils/api';
import { Upload, X, ChevronRight, ChevronLeft, Check, MapPin, DollarSign, Clock, Image } from 'lucide-react';

const STEPS = ['النوع والفئة', 'المعلومات الأساسية', 'الموقع والسعر', 'الصور', 'المراجعة'];

export default function AddListingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [images, setImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const { register, handleSubmit, watch, formState: { errors } } = useForm();

  const { data: cats } = useQuery('categories', () => api.get('/categories').then(r => r.data));

  const createMutation = useMutation(
    async (formData) => {
      const fd = new FormData();
      Object.keys(formData).forEach(k => fd.append(k, formData[k]));
      images.forEach(img => fd.append('images', img));
      return api.post('/listings', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    {
      onSuccess: () => navigate('/dashboard/listings')
    }
  );

  const listingType = watch('listing_type');

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 10) return alert('الحد الأقصى 10 صور');
    setImages(prev => [...prev, ...files]);
    const newPreviews = files.map(f => URL.createObjectURL(f));
    setPreviews(prev => [...prev, ...newPreviews]);
  };

  const removeImage = (idx) => {
    setImages(prev => prev.filter((_, i) => i !== idx));
    setPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  const onSubmit = (data) => {
    if (step < STEPS.length - 1) { setStep(s => s + 1); return; }
    createMutation.mutate(data);
  };

  const filteredCats = cats?.categories?.filter(c =>
    !listingType || c.type === listingType || c.type === 'all'
  ) || [];

  return (
    <div className="max-w-2xl mx-auto" dir="rtl">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white mb-6">إضافة إعلان جديد</h1>
        {/* Steps */}
        <div className="flex items-center gap-2">
          {STEPS.map((s, i) => (
            <div key={i} className="flex items-center gap-2 flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 transition-colors ${i < step ? 'bg-primary-500 text-white' : i === step ? 'bg-primary-500 text-white ring-4 ring-primary-500/30' : 'bg-slate-700 text-slate-400'}`}>
                {i < step ? <Check size={14} /> : i + 1}
              </div>
              <span className={`text-xs hidden sm:block ${i === step ? 'text-white' : 'text-slate-500'}`}>{s}</span>
              {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 transition-colors ${i < step ? 'bg-primary-500' : 'bg-slate-700'}`} />}
            </div>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="glass rounded-2xl p-6 space-y-5">

          {/* Step 0: Type & Category */}
          {step === 0 && (
            <>
              <div>
                <label className="block text-sm text-slate-400 mb-3">نوع الإعلان *</label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { v: 'venue', label: 'ملعب / منشأة', emoji: '🏟️' },
                    { v: 'equipment', label: 'معدات / أجهزة', emoji: '⚙️' },
                    { v: 'service', label: 'خدمة رياضية', emoji: '🎯' },
                  ].map(opt => (
                    <label key={opt.v} className={`cursor-pointer rounded-xl p-4 text-center border-2 transition-colors ${watch('listing_type') === opt.v ? 'border-primary-500 bg-primary-500/10' : 'border-slate-700 bg-slate-800/50 hover:border-slate-500'}`}>
                      <input type="radio" value={opt.v} {...register('listing_type', { required: true })} className="hidden" />
                      <div className="text-3xl mb-2">{opt.emoji}</div>
                      <div className="text-sm text-white font-medium">{opt.label}</div>
                    </label>
                  ))}
                </div>
                {errors.listing_type && <p className="text-red-400 text-xs mt-1">يرجى اختيار النوع</p>}
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-2">الفئة *</label>
                <select {...register('category_id', { required: true })} className="form-input w-full">
                  <option value="">اختر الفئة</option>
                  {filteredCats.map(c => (
                    <option key={c.id} value={c.id}>{c.name_ar}</option>
                  ))}
                </select>
                {errors.category_id && <p className="text-red-400 text-xs mt-1">يرجى اختيار الفئة</p>}
              </div>
            </>
          )}

          {/* Step 1: Basic Info */}
          {step === 1 && (
            <>
              <div>
                <label className="block text-sm text-slate-400 mb-2">عنوان الإعلان *</label>
                <input {...register('title', { required: true, minLength: 5 })} className="form-input w-full" placeholder="مثال: ملعب بادل مضاء بالكامل - النعيمية" />
                {errors.title && <p className="text-red-400 text-xs mt-1">عنوان يجب أن يكون 5 أحرف على الأقل</p>}
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">الوصف التفصيلي *</label>
                <textarea {...register('description', { required: true })} rows={5} className="form-input w-full resize-none" placeholder="اكتب وصفاً تفصيلياً للمرفق أو المعدة أو الخدمة..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">المساحة (م²)</label>
                  <input type="number" {...register('area')} className="form-input w-full" placeholder="اختياري" />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">السعة (أشخاص)</label>
                  <input type="number" {...register('capacity')} className="form-input w-full" placeholder="اختياري" />
                </div>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">المرافق والميزات</label>
                <textarea {...register('amenities')} rows={3} className="form-input w-full resize-none" placeholder="مثال: مواقف سيارات، تكييف، دورات مياه، غرف تبديل..." />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">الشروط والأحكام</label>
                <textarea {...register('rules')} rows={3} className="form-input w-full resize-none" placeholder="شروط الحجز والاستخدام..." />
              </div>
            </>
          )}

          {/* Step 2: Location & Pricing */}
          {step === 2 && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">الدولة *</label>
                  <select {...register('country', { required: true })} className="form-input w-full">
                    <option value="">اختر الدولة</option>
                    <option value="SA">المملكة العربية السعودية</option>
                    <option value="AE">الإمارات العربية المتحدة</option>
                    <option value="KW">الكويت</option>
                    <option value="QA">قطر</option>
                    <option value="BH">البحرين</option>
                    <option value="OM">عُمان</option>
                    <option value="JO">الأردن</option>
                    <option value="EG">مصر</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">المدينة *</label>
                  <input {...register('city', { required: true })} className="form-input w-full" placeholder="مثال: الرياض" />
                </div>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">العنوان التفصيلي *</label>
                <input {...register('address', { required: true })} className="form-input w-full" placeholder="الحي، الشارع، رقم المبنى..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">خط العرض (اختياري)</label>
                  <input type="number" step="any" {...register('latitude')} className="form-input w-full" placeholder="24.7136" />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">خط الطول (اختياري)</label>
                  <input type="number" step="any" {...register('longitude')} className="form-input w-full" placeholder="46.6753" />
                </div>
              </div>
              <hr className="border-slate-700" />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">السعر بالساعة *</label>
                  <input type="number" step="0.01" {...register('price_per_hour', { required: true, min: 1 })} className="form-input w-full" placeholder="150" />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">العملة</label>
                  <select {...register('currency')} className="form-input w-full">
                    <option value="SAR">ريال سعودي</option>
                    <option value="AED">درهم إماراتي</option>
                    <option value="KWD">دينار كويتي</option>
                    <option value="QAR">ريال قطري</option>
                    <option value="USD">دولار أمريكي</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">الحد الأدنى للحجز (ساعات)</label>
                  <input type="number" {...register('min_booking_hours')} defaultValue={1} className="form-input w-full" />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">الحد الأقصى للحجز (ساعات)</label>
                  <input type="number" {...register('max_booking_hours')} className="form-input w-full" placeholder="اختياري" />
                </div>
              </div>
            </>
          )}

          {/* Step 3: Images */}
          {step === 3 && (
            <div>
              <label className="block text-sm text-slate-400 mb-3">صور الإعلان (حتى 10 صور)</label>
              <label className="block cursor-pointer border-2 border-dashed border-slate-600 hover:border-primary-500 rounded-xl p-8 text-center transition-colors">
                <input type="file" multiple accept="image/*" onChange={handleImageChange} className="hidden" />
                <Upload size={36} className="text-slate-500 mx-auto mb-3" />
                <p className="text-slate-400">اضغط لإضافة صور أو اسحبها هنا</p>
                <p className="text-slate-600 text-sm mt-1">JPG, PNG - حتى 5MB لكل صورة</p>
              </label>
              {previews.length > 0 && (
                <div className="grid grid-cols-3 gap-3 mt-4">
                  {previews.map((src, i) => (
                    <div key={i} className="relative aspect-square rounded-xl overflow-hidden">
                      <img src={src} alt="" className="w-full h-full object-cover" />
                      {i === 0 && <span className="absolute top-1 right-1 bg-primary-500 text-white text-xs px-2 py-0.5 rounded-full">رئيسية</span>}
                      <button type="button" onClick={() => removeImage(i)} className="absolute top-1 left-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600">
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 4: Review */}
          {step === 4 && (
            <div className="space-y-4">
              <h3 className="text-lg font-bold text-white">مراجعة البيانات</h3>
              <div className="space-y-3 text-sm">
                {[
                  { label: 'نوع الإعلان', value: { venue: 'ملعب/منشأة', equipment: 'معدات', service: 'خدمة' }[watch('listing_type')] },
                  { label: 'العنوان', value: watch('title') },
                  { label: 'المدينة', value: watch('city') },
                  { label: 'السعر', value: `${watch('price_per_hour')} ${watch('currency') || 'SAR'}/ساعة` },
                  { label: 'عدد الصور', value: `${images.length} صورة` },
                ].map(row => (
                  <div key={row.label} className="flex justify-between items-center p-3 bg-slate-800/50 rounded-lg">
                    <span className="text-slate-400">{row.label}</span>
                    <span className="text-white font-medium">{row.value || '—'}</span>
                  </div>
                ))}
              </div>
              {createMutation.isError && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
                  حدث خطأ أثناء الإنشاء. يرجى المحاولة مرة أخرى.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex gap-3 mt-6">
          {step > 0 && (
            <button type="button" onClick={() => setStep(s => s - 1)} className="btn-secondary flex items-center gap-2">
              <ChevronRight size={18} />
              السابق
            </button>
          )}
          <button
            type="submit"
            disabled={createMutation.isLoading}
            className="flex-1 btn-primary flex items-center justify-center gap-2"
          >
            {step === STEPS.length - 1 ? (
              createMutation.isLoading ? 'جارٍ النشر...' : 'نشر الإعلان'
            ) : (
              <>التالي <ChevronLeft size={18} /></>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
